import React, { useEffect, useState } from "react";
import { useConfirm } from "material-ui-confirm";
import { useTranslation } from "react-i18next";
import {
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Modal,
  Switch,
  TextField,
  Typography,
  useTheme,
  Alert,
  AlertTitle,
  Link
} from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";

import ListResponsive from "../../../components/other/ListResponsive";
import { userDisplayName } from "./EventFunctions";
import StyledMenu from "../../../components/layout/StyledMenu";
import GenericPicker from "../../../components/utilities/GenericPicker";
import { handlePagingAll } from "../../../components/api/APIUtilities";

import * as Yup from "yup";
import { FieldArray, Formik } from "formik";
import ProgressModal from "../../../components/modals/ProgressModal";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import { EventSoftScorePlayerEntry } from "./SubComponents/EventSoftScoreComponents";
import { userPlatformSearch } from "../utils";
import {
  BCPBarsSort,
  BCPFilter,
  BCPFilterSlash,
  BCPPen,
  BCPSquarePlus,
  BCPXmark
} from "../../../components/icons/bcp/BCPIcons";
import {
  getData,
  postData
} from "../../../components/utilities/UtilityFunctions";
import { useInView } from "react-intersection-observer";
import { FiltersMenu, SortMenu } from "./SubComponents/EventSoftScoreMenus";
import PromptIfDirty from "../../../components/utilities/PromptIfDirty";

export default function EventSoftScores(props) {
  const { organize, setActiveTab, event, authContext, setEvent } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventSoftScores"
  });

  const [softScore, setSoftScore] = useState(null);
  const [softScoreFilter, setSoftScoreFilter] = useState("");
  const [tableHeadings, setTableHeadings] = useState([]);
  const [softScoreDict, setSoftScoreDict] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [refreshPlayers, setRefreshPlayers] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [playerDict, setPlayerDict] = useState({});
  const [players, setPlayers] = useState(null);

  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const [submittedStatus, setSubmittedStatus] = useState(0);

  const [filtersAnchorEl, setFiltersAnchorEl] = useState(null);
  const [filterType, setFilterType] = useState("");

  const [valuesDirty, setValuesDirty] = useState(false);
  const [updatedValues, setUpdatedValues] = useState({});
  const [saveBoxRef, saveBoxInView] = useInView({
    threshold: 0
  });

  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [sortedPlayers, setSortedPlayers] = useState(null);

  const fetchPlayers = async () => {
    var apiPlayers = await getData(`/events/${event.id}/players`, {
      placings: true
    });
    return apiPlayers?.active;
  };
  const fetchSoftScorePairings = async () => {
    var apiSoftScorePairings = await handlePagingAll("/pairings", {
      eventId: event.id,
      pairingType: "SoftScorePairing"
    });
    return apiSoftScorePairings;
  };
  const fetchParallel = async () => {
    const taskFetchSoftScorePairings = fetchSoftScorePairings();
    const taskFetchPlayers = fetchPlayers();
    return {
      apiPlayers: await taskFetchPlayers,
      apiSoftScorePairings: await taskFetchSoftScorePairings
    };
  };
  const handlePlayers = async () => {
    var apiData = await fetchParallel();
    var apiPlayers = apiData?.apiPlayers || [];
    var apiSoftScorePairings = apiData?.apiSoftScorePairings || [];

    var softScorePairingsByPlayer = {};
    var softScorePairingsByOpponent = {};
    if (apiSoftScorePairings?.length > 0) {
      for (let softScorePairing of apiSoftScorePairings) {
        if (softScorePairing?.playerId) {
          if (!softScorePairingsByPlayer[softScorePairing.playerId]) {
            softScorePairingsByPlayer[softScorePairing.playerId] = {};
          }
          if (softScorePairing.softScoreId) {
            if (
              !softScorePairingsByPlayer[softScorePairing.playerId][
                softScorePairing.softScoreId
              ]
            ) {
              softScorePairingsByPlayer[softScorePairing.playerId][
                softScorePairing.softScoreId
              ] = [];
            }
            softScorePairingsByPlayer[softScorePairing.playerId][
              softScorePairing.softScoreId
            ].push(softScorePairing);
          }
        }

        if (softScorePairing?.scores?.length > 0) {
          for (let score of softScorePairing.scores) {
            if (score?.playerId) {
              if (!softScorePairingsByOpponent[score.playerId]) {
                softScorePairingsByOpponent[score.playerId] = {};
              }
              if (softScorePairing.softScoreId) {
                if (
                  !softScorePairingsByOpponent[score.playerId][
                    softScorePairing.softScoreId
                  ]
                ) {
                  softScorePairingsByOpponent[score.playerId][
                    softScorePairing.softScoreId
                  ] = [];
                }
                softScorePairingsByOpponent[score.playerId][
                  softScorePairing.softScoreId
                ].push(score);
              }
            }
          }
        }
      }
    }

    let newPlayerDict = {};
    for (let i = 0; i < apiPlayers?.length; i++) {
      if (!apiPlayers[i].name) {
        apiPlayers[i].name = `${apiPlayers[i].firstName || ""} ${
          apiPlayers[i].lastName || ""
        }`.trim();
      }
      apiPlayers[i].softScorePairingsBySoftScore =
        softScorePairingsByPlayer[apiPlayers[i].id];
      apiPlayers[i].softScorePairingsReceivedBySoftScore =
        softScorePairingsByOpponent[apiPlayers[i].id];
      newPlayerDict[apiPlayers[i].id] = apiPlayers[i];
    }

    apiPlayers.sort((a, b) => {
      var nameA = a.name?.toLowerCase();
      var nameB = b.name?.toLowerCase();

      if (nameA > nameB) return 1;
      if (nameA < nameB) return -1;
      return 0;
    });

    setPlayerDict(newPlayerDict);
    setPlayers(apiPlayers);
  };

  const updatePlayers = (playerId, softScores) => {
    var newPlayers = [];
    players.forEach((player) => {
      if (player.id === playerId) player.softScores = softScores;
      newPlayers.push(player);
    });
    setPlayers(newPlayers);
  };

  const updateScores = async () => {
    if (Object.keys(updatedValues).length) {
      setSyncing(true);

      const tempPlayers = players ? [...players] : [];

      for (const playerId in updatedValues) {
        // Updates `players` array with updated soft scores. This will re-sort the list if there are any changes
        if (tempPlayers?.length) {
          for (let player of tempPlayers) {
            if (player?.id === playerId) {
              if (player?.softScores?.length) {
                player.softScores.forEach((score) => {
                  for (const label in updatedValues[playerId]) {
                    // If player has an existing value for specific soft score
                    if (score?.name === label) {
                      const updatedValue = updatedValues[playerId][score.name];
                      if (updatedValue) {
                        score.value = updatedValue.value;
                      }
                    } else {
                      // If player has existing soft scores but not this specific score
                      player.softScores.push({
                        name: label,
                        value: updatedValues[playerId][label].value
                      });
                    }
                  }
                });
              } else {
                // If player has no soft scores whatsoever
                let newSoftScores = [];

                for (const label in updatedValues[playerId]) {
                  newSoftScores.push({
                    name: label,
                    value: updatedValues[playerId][label].value
                  });
                }

                player.softScores = newSoftScores;
              }

              // Stops loop once a playerId has matched a player.id
              break;
            }
          }
        }

        for (const label in updatedValues[playerId]) {
          try {
            await postData(`/players/${playerId}/softScore`, {
              body: {
                softScoreId: updatedValues[playerId][label].softScoreId,
                value: updatedValues[playerId][label].value
              }
            });
          } catch (err) {
            postNotification("error", err);
          }
        }
      }

      if (tempPlayers?.length) setPlayers(tempPlayers);
      setUpdatedValues({});
      setValuesDirty(false);
      setSyncing(false);
    }
  };

  const sortPlayers = (_players, _sortType) => {
    const sortArray = (arr, type) => {
      return arr.sort((a, b) => {
        if (type === "asc" || type === "desc") {
          let scoreA = a?.softScores?.filter((sc) => {
            if (sc?.name === softScoreFilter) return true;
            else return false || 0;
          })?.[0]?.value;
          let scoreB =
            b?.softScores?.filter((sc) => {
              if (sc?.name === softScoreFilter) return true;
              else return false;
            })?.[0]?.value || 0;

          if (typeof scoreA === "string") scoreA = parseInt(scoreA);
          if (typeof scoreB === "string") scoreB = parseInt(scoreB);

          if (scoreA > scoreB) return type === "asc" ? 1 : -1;
          if (scoreA < scoreB) return type === "asc" ? -1 : 1;
          return 0;
        }

        // "a-z" sort type is the default sorting, which means its not actually a sort type at all but rather just null.
        if (type === "z-a") {
          const nameA = a?.name;
          const nameB = b?.name;

          if (nameA > nameB) return -1;
          if (nameA < nameB) return 1;
          return 0;
        }

        return 0;
      });
    };

    if (_players?.length) {
      let newArr = _players.filter(function (el) {
        if (softScoreFilter && softScoreFilter !== "") {
          return Boolean(!el.softScores?.[softScoreFilter]);
        } else {
          var val = false;
          Object.keys(softScoreDict).forEach((ss) => {
            if (!el.softScores?.[ss]) {
              val = true;
              return true;
            }
          });
          return val;
        }
      });

      if (_sortType) {
        return sortArray(newArr, _sortType);
      } else {
        return _players;
      }
    }

    return [];
  };

  useEffect(() => {
    if (!valuesDirty) {
      if (Object.keys(updatedValues).length) setValuesDirty(true);
    }
    // eslint-disable-next-line
  }, [updatedValues]);

  useEffect(() => {
    const check = async () => {
      try {
        if (!organize) return;
        if (!event?.gameSystem?.supportSoftScores) {
          postNotification(
            "error",
            `${event?.gameSystem?.name} does not support Soft Scores`
          );
          setActiveTab(null);
          return;
        }
        if (event) {
          var newTableHeadings = [];
          var newSoftScoreDict = {};
          if (event.softScores) {
            event.softScores.forEach((ss) => {
              newTableHeadings.push(ss.name);
              newSoftScoreDict[ss.name] = ss;
            });
          }
          setTableHeadings(newTableHeadings);
          setSoftScoreDict(newSoftScoreDict);
          await handlePlayers();
        }
      } catch (err) {
        postNotification("error", err);
      }
      setSubLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, [event?.softScores]);

  useEffect(() => {
    if (tableHeadings.length === 1) {
      setSoftScoreFilter(tableHeadings[0]);
      setFilterType(tableHeadings[0]);
    }
  }, [tableHeadings]);

  useEffect(() => {
    const check = async () => {
      try {
        if (!refreshPlayers) return;
        if (!organize) return;
        if (event) {
          if (!players?.length) {
            setSubLoading(true);
          } else {
            setSyncing(true);
          }
          await handlePlayers();
          setSyncing(false);
        }
      } catch (err) {
        postNotification("error", err);
      }
      setSubLoading(false);
      setRefreshPlayers(false);
    };
    check();
    // eslint-disable-next-line
  }, [refreshPlayers]);

  useEffect(() => {
    if (!softScoreFilter) {
      setSortedPlayers(null);
      setSortType(null);
    }

    if (sortType && players) {
      setSortedPlayers(sortPlayers(players, sortType));
    } else {
      setSortedPlayers(null);
    }
    // eslint-disable-next-line
  }, [sortType, softScoreFilter, players]);

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <PromptIfDirty when={valuesDirty} message={t("unsavedChangesWarning")} />
      <Grid container spacing={2}>
        {organize && (
          <>
            <Grid item xs={10}>
              <Typography variant="h5">
                {t("selectedSoftScore")}{" "}
                {softScoreFilter ? softScoreFilter : "-------"}
              </Typography>
            </Grid>

            {softScoreFilter && (
              <Grid item xs={2} sx={{ textAlign: "right" }}>
                <IconButton
                  onClick={(e) => {
                    setSortAnchorEl(e.target);
                  }}
                >
                  <BCPBarsSort />
                </IconButton>
              </Grid>
            )}

            <Hidden mdDown>
              <Grid item xs={12}>
                <ButtonGroup variant="contained" fullWidth>
                  <Button
                    onClick={() => {
                      setSoftScore({});
                    }}
                    startIcon={<BCPSquarePlus />}
                  >
                    {t("addNew")}
                  </Button>
                  <Button
                    onClick={() => {
                      setSoftScore(softScoreDict[softScoreFilter]);
                    }}
                    disabled={!softScoreFilter}
                    variant="outlined"
                  >
                    {t("edit")}
                  </Button>
                  {organize &&
                    (authContext?.authState?.identity?.isAdmin === true ||
                      authContext?.authState?.identity?.adminGameSystems?.includes(
                        event?.gameSystemId
                      )) && (
                      <Button
                        onClick={(e) => {
                          setOptionsAnchorEl(e.currentTarget);
                        }}
                        aria-expanded={
                          Boolean(optionsAnchorEl) ? true : undefined
                        }
                        disabled={
                          (authContext?.authState?.identity?.isAdmin === true ||
                            authContext?.authState?.identity?.adminGameSystems?.includes(
                              event?.gameSystemId
                            )) &&
                          softScoreFilter &&
                          softScoreDict?.[softScoreFilter]
                            ? false
                            : true
                        }
                        // Changes color to maintain consistent pattern depending on if "activate/deactivate" button is rendered
                        variant={
                          softScoreDict[softScoreFilter]?.playerScored &&
                          softScoreDict[softScoreFilter]?.oncePerEvent
                            ? "contained"
                            : "outlined"
                        }
                      >
                        {t("options")}
                      </Button>
                    )}
                  {softScoreDict[softScoreFilter]?.playerScored &&
                    softScoreDict[softScoreFilter]?.oncePerEvent && (
                      <Button
                        onClick={async () => {
                          setProcessing(true);
                          let ss = softScoreDict[softScoreFilter];
                          try {
                            let updatedEvent = await postData(
                              `/events/${event.id}/softScore`,
                              {
                                body: {
                                  softScoreId: ss.id,
                                  active: !ss.active
                                }
                              }
                            );
                            setEvent({ ...event, ...updatedEvent });
                          } catch (err) {
                            postNotification("error", err);
                          }
                          setProcessing(false);
                        }}
                        disabled={!softScoreFilter}
                        variant="outlined"
                      >
                        {softScoreDict[softScoreFilter]?.active
                          ? t("deactivate")
                          : t("activate")}
                      </Button>
                    )}

                  <Button
                    onClick={(e) => {
                      setFiltersAnchorEl(e.currentTarget);
                    }}
                    aria-expanded={Boolean(optionsAnchorEl) ? true : undefined}
                    endIcon={
                      softScoreFilter ? (
                        <BCPFilter sx={{ fontSize: "20px", width: "20px" }} />
                      ) : (
                        <BCPFilterSlash
                          sx={{ fontSize: "24px", width: "24px" }}
                        />
                      )
                    }
                  >
                    {t("softScores")}
                  </Button>
                </ButtonGroup>
              </Grid>
            </Hidden>
            <Hidden mdUp>
              <Grid item xs={12}>
                <ButtonGroup variant="contained" fullWidth>
                  <Button
                    onClick={() => {
                      setSoftScore({});
                    }}
                    startIcon={<BCPSquarePlus />}
                  >
                    {t("addNew")}
                  </Button>
                  <Button
                    onClick={() => {
                      setSoftScore(softScoreDict[softScoreFilter]);
                    }}
                    disabled={!softScoreFilter}
                    variant="outlined"
                  >
                    {t("edit")}
                  </Button>

                  <Button
                    onClick={(e) => {
                      setFiltersAnchorEl(e.currentTarget);
                    }}
                    aria-expanded={Boolean(optionsAnchorEl) ? true : undefined}
                    endIcon={
                      softScoreFilter ? (
                        <BCPFilter sx={{ fontSize: "20px", width: "20px" }} />
                      ) : (
                        <BCPFilterSlash
                          sx={{ fontSize: "24px", width: "24px" }}
                        />
                      )
                    }
                  >
                    {t("softScores")}
                  </Button>
                </ButtonGroup>
              </Grid>
              <Grid item xs={12}>
                <ButtonGroup variant="contained" fullWidth>
                  {organize &&
                    (authContext?.authState?.identity?.isAdmin === true ||
                      authContext?.authState?.identity?.adminGameSystems?.includes(
                        event?.gameSystemId
                      )) && (
                      <Button
                        onClick={(e) => {
                          setOptionsAnchorEl(e.currentTarget);
                        }}
                        aria-expanded={
                          Boolean(optionsAnchorEl) ? true : undefined
                        }
                        disabled={
                          (authContext?.authState?.identity?.isAdmin === true ||
                            authContext?.authState?.identity?.adminGameSystems?.includes(
                              event?.gameSystemId
                            )) &&
                          softScoreFilter &&
                          softScoreDict?.[softScoreFilter]
                            ? false
                            : true
                        }
                        // Changes color to maintain consistent pattern depending on if "activate/deactivate" button is rendered
                        variant={
                          softScoreDict[softScoreFilter]?.playerScored &&
                          softScoreDict[softScoreFilter]?.oncePerEvent
                            ? "contained"
                            : "outlined"
                        }
                      >
                        {t("options")}
                      </Button>
                    )}
                  {softScoreDict[softScoreFilter]?.playerScored &&
                    softScoreDict[softScoreFilter]?.oncePerEvent && (
                      <Button
                        onClick={async () => {
                          setProcessing(true);
                          let ss = softScoreDict[softScoreFilter];
                          try {
                            let updatedEvent = await postData(
                              `/events/${event.id}/softScore`,
                              {
                                body: {
                                  softScoreId: ss.id,
                                  active: !ss.active
                                }
                              }
                            );
                            setEvent({ ...event, ...updatedEvent });
                          } catch (err) {
                            postNotification("error", err);
                          }
                          setProcessing(false);
                        }}
                        disabled={!softScoreFilter}
                        variant="outlined"
                      >
                        {softScoreDict[softScoreFilter]?.active
                          ? t("deactivate")
                          : t("activate")}
                      </Button>
                    )}
                </ButtonGroup>
              </Grid>
            </Hidden>
          </>
        )}

        <Grid item xs={12}>
          <ListResponsive
            originalList={
              subLoading
                ? null
                : sortedPlayers && sortedPlayers?.length
                  ? sortedPlayers
                  : submittedStatus === 0
                    ? players
                    : submittedStatus === 1
                      ? players.filter(function (el) {
                          if (softScoreFilter && softScoreFilter !== "") {
                            return Boolean(el.softScores?.[softScoreFilter]);
                          } else {
                            var val = true;
                            Object.keys(softScoreDict).forEach((ss) => {
                              if (!el.softScores?.[ss]) {
                                val = false;
                                return true;
                              }
                            });
                            return val;
                          }
                        })
                      : players.filter(function (el) {
                          if (softScoreFilter && softScoreFilter !== "") {
                            return Boolean(!el.softScores?.[softScoreFilter]);
                          } else {
                            var val = false;
                            Object.keys(softScoreDict).forEach((ss) => {
                              if (!el.softScores?.[ss]) {
                                val = true;
                                return true;
                              }
                            });
                            return val;
                          }
                        })
            }
            doSearch
            doPages
            initialRowsPerPage={24}
            searchProps={{
              label: t("searchPlayers"),
              performSearch: userPlatformSearch
            }}
            rowProps={{
              mapFunction: (row, index) => (
                <Grid item xs={12} key={row?.id || index}>
                  <ListRow
                    row={playerDict?.[row?.id]}
                    index={index}
                    tableHeadings={tableHeadings}
                    softScoreFilter={softScoreFilter}
                    event={event}
                    softScoreDict={softScoreDict}
                    setSyncing={setSyncing}
                    updatePlayers={updatePlayers}
                    setRefreshPlayers={setRefreshPlayers}
                    updatedValues={updatedValues}
                    setUpdatedValues={setUpdatedValues}
                    syncing={syncing}
                  />
                </Grid>
              )
            }}
          />
        </Grid>

        {organize && (
          <Grid item xs={12}>
            <Button
              ref={saveBoxRef}
              fullWidth
              color="success"
              variant="contained"
              onClick={async () => {
                await updateScores();
              }}
              disabled={!valuesDirty || syncing}
            >
              {t("save")}
            </Button>
          </Grid>
        )}
      </Grid>

      {organize && (
        <>
          {!saveBoxInView && (
            <Box
              sx={{
                position: "fixed",
                zIndex: 999,
                p: 2,
                bgcolor: "background.paper",
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                boxShadow: 3,
                bottom: 0,
                left: [0, 0, 0, 256],
                width: ["100%", "100%", "100%", "calc(100% - 256px)"]
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Button
                  fullWidth
                  color="success"
                  variant="contained"
                  type="submit"
                  onClick={async () => {
                    await updateScores();
                  }}
                  disabled={!valuesDirty || syncing}
                >
                  {t("save")}
                </Button>
              </Box>
            </Box>
          )}
          <OptionsMenu
            authContext={authContext}
            anchorEl={optionsAnchorEl}
            setAnchorEl={setOptionsAnchorEl}
            submittedStatus={submittedStatus}
            setSubmittedStatus={setSubmittedStatus}
            softScoreDict={softScoreDict}
            softScoreFilter={softScoreFilter}
            players={players}
            event={event}
            setRefreshPlayers={setRefreshPlayers}
            setSyncing={setSyncing}
            updatePlayers={updatePlayers}
          />
          {syncing && (
            <Box
              sx={{
                position: "fixed",
                top: "100px",
                right: "20px",
                zIndex: "999",
                p: 2,
                border: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: 1,
                boxShadow: 1,
                backgroundColor: "info.light",
                color: "info.contrastText",
                display: "flex",
                alignItems: "center",
                padding: 1
              }}
            >
              <Typography>
                {t("syncing")} <CircularProgress size={18} />
              </Typography>
            </Box>
          )}

          {softScore && (
            <SoftScoreModal
              authContext={authContext}
              event={event}
              setEvent={setEvent}
              softScore={softScore}
              setSoftScore={setSoftScore}
            />
          )}

          <FiltersMenu
            anchorEl={filtersAnchorEl}
            setAnchorEl={setFiltersAnchorEl}
            filterType={filterType}
            setFilterType={setFilterType}
            setSoftScoreFilter={setSoftScoreFilter}
            tableHeadings={tableHeadings}
          />

          <SortMenu
            anchorEl={sortAnchorEl}
            setAnchorEl={setSortAnchorEl}
            sortType={sortType}
            setSortType={setSortType}
          />
        </>
      )}

      {processing && <ModalLoadingFull />}
    </Container>
  );
}

const ListRow = (props) => {
  const {
    heading,
    row,
    tableHeadings,
    softScoreFilter,
    event,
    softScoreDict,
    setSyncing,
    updatePlayers,
    setRefreshPlayers,
    updatedValues,
    setUpdatedValues,
    syncing
  } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventSoftScores"
  });

  return (
    <>
      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          sm
          sx={{
            display: "flex",
            alignItems: "center"
          }}
        >
          <Typography>
            {heading
              ? t("player")
              : userDisplayName(
                  row?.firstName || row?.user?.firstName,
                  row?.lastName || row?.user?.lastName
                )}
          </Typography>
        </Grid>
        {tableHeadings.map((softScore, index) => {
          if (
            Boolean(
              softScoreFilter &&
              softScoreFilter !== "" &&
              softScoreFilter !== softScore
            )
          ) {
            return <></>;
          }

          if (heading) {
            return (
              <Grid
                key={index}
                item
                xs
                sx={{ textAlign: "center", width: "200px", maxWidth: "200px" }}
              >
                {softScore}
              </Grid>
            );
          }

          var score = "";
          if (
            row.softScores &&
            row.softScores.filter((sc) => sc.name === softScore)?.[0]
          ) {
            score = row.softScores.filter((sc) => sc.name === softScore)?.[0]
              .value;
          }
          return (
            <Grid
              key={index}
              item
              xs
              sx={{
                width: "200px",
                maxWidth: "200px"
              }}
            >
              <SoftScoreCell
                playerId={row.id}
                player={row}
                event={event}
                softScoreDict={softScoreDict}
                label={softScore}
                score={score}
                setSyncing={setSyncing}
                updatePlayers={updatePlayers}
                setRefreshPlayers={setRefreshPlayers}
                updatedValues={updatedValues}
                setUpdatedValues={setUpdatedValues}
                syncing={syncing}
              />
            </Grid>
          );
        })}
      </Grid>
      <Divider sx={{ mb: 1, mt: 1 }} />
    </>
  );
};

function OptionsMenu(props) {
  const {
    event,
    authContext,
    anchorEl,
    setAnchorEl,
    softScoreDict,
    softScoreFilter,
    setRefreshPlayers,
    players
  } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventSoftScores"
  });
  const [importModal, setImportModal] = useState(false);
  const open = Boolean(anchorEl);

  return (
    <>
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          "aria-labelledby": "options-button"
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        {(authContext?.authState?.identity?.isAdmin === true ||
          authContext?.authState?.identity?.adminGameSystems?.includes(
            event?.gameSystemId
          )) &&
          softScoreFilter &&
          softScoreDict?.[softScoreFilter] && (
            <MenuItem
              onClick={async (e) => {
                setImportModal(true);
                setAnchorEl(null);
              }}
              disableRipple
            >
              {t("bulkUploadSoftScores")}
            </MenuItem>
          )}
      </StyledMenu>
      <BulkSoftScoresModal
        open={importModal}
        setOpen={setImportModal}
        players={players}
        setRefreshPlayers={setRefreshPlayers}
        softScore={softScoreDict?.[softScoreFilter]}
      />
    </>
  );
}

function SoftScoreCell(props) {
  const {
    event,
    player,
    playerId,
    softScoreDict,
    label,
    score,
    setRefreshPlayers,
    updatedValues,
    setUpdatedValues,
    syncing
  } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventSoftScores"
  });

  const theme = useTheme();
  const [value, setValue] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);

  const handleChange = (val) => {
    if (val === value) return;
    setValue(val);
    setIsUpdated(true);

    let newVals = {};

    if (updatedValues[playerId]) {
      newVals = { ...updatedValues[playerId] };
    }

    newVals[label] = {
      softScoreId: softScoreDict[label]?.id,
      value: val || 0
    };

    setUpdatedValues({
      ...updatedValues,
      [playerId]: newVals
    });
  };

  var inputProps = softScoreDict[label].playerScored
    ? {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={(e) => {
                setModalOpen(true);
              }}
            >
              <BCPPen />
            </IconButton>
          </InputAdornment>
        )
      }
    : softScoreDict[label]?.scorecardId
      ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                href={`/softscore/${softScoreDict[label].id}/${playerId}`}
              >
                <BCPPen />
              </IconButton>
            </InputAdornment>
          )
        }
      : softScoreDict[label].maxValue
        ? {
            inputProps: { max: softScoreDict[label].maxValue },
            endAdornment: (
              <InputAdornment position="end">
                {isUpdated && syncing && <CircularProgress />}
                <Button
                  variant="outlined"
                  sx={{
                    minWidth: "unset",
                    padding: "4px 4px"
                  }}
                  onClick={(e) => {
                    handleChange(softScoreDict[label].maxValue);
                  }}
                  edge="end"
                >
                  {t("max")}
                </Button>
              </InputAdornment>
            )
          }
        : {
            endAdornment: (
              <InputAdornment position="end">
                {isUpdated && syncing && <CircularProgress />}
              </InputAdornment>
            )
          };

  useEffect(() => {
    if (softScoreDict[label].playerScored) {
      let val = 0;
      let softScoreId = softScoreDict[label]?.id;
      if (
        softScoreId &&
        player?.softScorePairingsReceivedBySoftScore?.[softScoreId]?.length
      ) {
        for (let softScoreReceived of player
          .softScorePairingsReceivedBySoftScore[softScoreId]) {
          val += softScoreReceived?.value || 0;
        }
      }
      setValue(val);
    } else {
      setValue(score !== undefined ? score : "");
    }
    // eslint-disable-next-line
  }, [player]);

  useEffect(() => {
    if (!Object.keys(updatedValues).length) {
      setIsUpdated(false);
    }
  }, [updatedValues]);

  return (
    <>
      <TextField
        name="value"
        label={isUpdated ? `${label} (edited)` : label}
        value={value}
        variant="outlined"
        fullWidth
        margin="normal"
        type="number"
        onBlur={(e) => {
          if (value === e.target.value) return;
          handleChange(e.target.value);
        }}
        onChange={(e) => {
          if (value === e.target.value) return;
          handleChange(e.target.value);
        }}
        InputProps={inputProps}
        disabled={
          softScoreDict[label]?.playerScored ||
          softScoreDict[label]?.scorecardId
        }
        sx={{
          "& .MuiInputBase-input": {
            WebkitTextFillColor:
              (softScoreDict[label]?.playerScored &&
                player?.softScorePairingsBySoftScore?.[softScoreDict[label]?.id]
                  ?.length > 0) ||
              (softScoreDict[label]?.scorecardId && value !== "")
                ? `${theme?.palette?.success.main} !important`
                : null,
            color:
              (softScoreDict[label]?.playerScored &&
                player?.softScorePairingsBySoftScore?.[softScoreDict[label]?.id]
                  ?.length > 0) ||
              (softScoreDict[label]?.scorecardId && value !== "")
                ? `${theme?.palette?.success.main} !important`
                : null
          },
          "& .MuiInputLabel-root": {
            WebkitTextFillColor:
              (softScoreDict[label]?.playerScored &&
                player?.softScorePairingsBySoftScore?.[softScoreDict[label]?.id]
                  ?.length > 0) ||
              (softScoreDict[label]?.scorecardId && value !== "")
                ? `${theme?.palette?.success.main} !important`
                : null,
            color:
              (softScoreDict[label]?.playerScored &&
                player?.softScorePairingsBySoftScore?.[softScoreDict[label]?.id]
                  ?.length > 0) ||
              (softScoreDict[label]?.scorecardId && value !== "")
                ? `${theme?.palette?.success.main} !important`
                : null
          },
          "& fieldset.MuiOutlinedInput-notchedOutline": {
            borderColor:
              (softScoreDict[label]?.playerScored &&
                player?.softScorePairingsBySoftScore?.[softScoreDict[label]?.id]
                  ?.length > 0) ||
              (softScoreDict[label]?.scorecardId && value !== "")
                ? `${theme?.palette?.success.main} !important`
                : null
          }
        }}
      />
      {isUpdated && syncing && (
        <Box
          sx={{
            position: "fixed",
            top: "100px",
            right: "20px",
            zIndex: "999",
            p: 2,
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "info.light",
            color: "info.contrastText",
            display: "flex",
            alignItems: "center",
            padding: 1
          }}
        >
          <Typography>
            Syncing <CircularProgress size={18} />
          </Typography>
        </Box>
      )}
      {modalOpen && (
        <PlayerSoftScores
          open={modalOpen}
          setOpen={setModalOpen}
          softScore={softScoreDict[label]}
          player={player}
          event={event}
          setRefreshPlayers={setRefreshPlayers}
        />
      )}
    </>
  );
}

const PlayerSoftScores = (props) => {
  const { open, setOpen, event, softScore, player, isTeam, setRefreshPlayers } =
    props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventSoftScores"
  });

  const [loading, setLoading] = useState(true);
  const [, setMySoftScorePairings] = useState(null);
  const [opponentSoftScorePairings, setOpponentSoftScorePairings] =
    useState(null);

  const [total, setTotal] = useState(null);

  const fetchMyPairings = async () => {};

  const fetchOpponentPairings = async () => {
    setTotal(null);
    setOpponentSoftScorePairings(null);
    let apiPairings = await handlePagingAll(
      "/pairings",
      isTeam
        ? {
            eventId: event.id,
            softScoreId: softScore.id,
            scoredTeamPlayerId: player.id,
            pairingType: "SoftScorePairing",
            "expand[]": ["teamPlayer"]
          }
        : {
            eventId: event.id,
            softScoreId: softScore.id,
            scoredPlayerId: player.id,
            pairingType: "SoftScorePairing",
            "expand[]": ["player"]
          }
    );
    if (apiPairings?.length > 0) {
      let apiTotal = 0;
      for (let i = 0; i < apiPairings.length; i++) {
        apiPairings[i].score = apiPairings[i].scores?.filter(
          (el) => el.playerId === player.id
        )?.[0];
        delete apiPairings[i].scores;
        apiTotal += apiPairings[i].score?.value || 0;
      }
      setTotal(apiTotal);
    }
    setOpponentSoftScorePairings(apiPairings);
  };

  const parallel = async () => {
    const taskMyPairings = fetchMyPairings();
    const taskOpponentPairings = fetchOpponentPairings();

    return {
      myPairings: await taskMyPairings,
      opponentPairings: await taskOpponentPairings
    };
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);

        await parallel();
      } catch (err) {
        postNotification("error", err);
      }
      setLoading(false);
    };

    setMySoftScorePairings(null);
    setOpponentSoftScorePairings(null);
    if (open) {
      fetch();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [open, player]);

  if (!open) return;

  return (
    <>
      <Modal
        disableAutoFocus
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "95vw",
            minHeight: "85vh",
            maxHeight: "85vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setOpen(false);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4">
                {player?.name
                  ? player.name
                  : `${player?.firstName || player?.user?.firstName || ""} ${
                      player?.lastName || player?.user?.lastName || ""
                    }`.trim()}
              </Typography>
              <Typography variant="h5">{softScore?.name}</Typography>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" component="h2">
                {t("scored")}
              </Typography>
            </Grid>
          </Grid>

          <EventSoftScorePlayerEntry
            organize
            player={player}
            event={event}
            softScoreId={softScore?.id}
            setRefreshPlayers={setRefreshPlayers}
          />

          <Grid
            container
            spacing={2}
            sx={{
              pt: 1,
              mt: 1,
              borderTop: "1px solid rgba(0, 0, 0, 0.12)",

              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" component="h2">
                {t("received")}
              </Typography>
            </Grid>
          </Grid>

          {opponentSoftScorePairings?.length > 0 ? (
            <>
              <Grid
                container
                sx={{
                  pb: 1,
                  mb: 2,
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                }}
              >
                <Grid item xs={6}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {t("player")}
                  </Typography>
                </Grid>

                <Grid item xs={3} sx={{ textAlign: "center" }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {t("round")}
                  </Typography>
                </Grid>

                <Grid item xs={3} sx={{ textAlign: "center" }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {t("score")}
                  </Typography>
                </Grid>
              </Grid>
              {opponentSoftScorePairings.map((softScorePairing) => (
                <Grid container>
                  <Grid item xs={6}>
                    {softScorePairing?.byePlayer
                      ? "--------"
                      : `${
                          softScorePairing?.player?.firstName ||
                          softScorePairing?.player?.user?.firstName ||
                          ""
                        } ${
                          softScorePairing?.player?.lastName ||
                          softScore?.player?.user?.lastName ||
                          ""
                        }`}
                  </Grid>

                  <Grid item xs={3} sx={{ textAlign: "center" }}>
                    {softScorePairing?.round !== undefined
                      ? softScorePairing.round
                      : "N/A"}
                  </Grid>

                  <Grid item xs={3} sx={{ textAlign: "center" }}>
                    {softScorePairing?.score?.value || 0}
                  </Grid>
                </Grid>
              ))}
              <Grid
                container
                sx={{
                  pt: 1,
                  mt: 2,
                  borderTop: "1px solid rgba(0, 0, 0, 0.12)"
                }}
              >
                <Grid item xs={6}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {t("total")}
                  </Typography>
                </Grid>

                <Grid item xs={3} sx={{ textAlign: "center" }}></Grid>

                <Grid item xs={3} sx={{ textAlign: "center" }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {total}
                  </Typography>
                </Grid>
              </Grid>
            </>
          ) : !loading ? (
            <Typography>{t("noSoftScorePairingRecords")}</Typography>
          ) : (
            <LinearProgress />
          )}
        </Box>
      </Modal>
    </>
  );
};

const BulkSoftScoresModal = (props) => {
  const { open, setOpen, softScore, players, setRefreshPlayers } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventSoftScores"
  });

  return (
    <Modal
      disableAutoFocus
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "95vw",
          minHeight: "95vh",
          maxHeight: "95vh",
          overflowY: "scroll",
          bgcolor: "background.paper",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24,
          p: 4
        }}
      >
        <IconButton
          aria-label="cancel"
          color="error"
          sx={{ position: "absolute", top: "4px", right: "4px" }}
          onClick={() => {
            setOpen(false);
          }}
        >
          <BCPXmark />
        </IconButton>
        <Grid
          container
          spacing={2}
          sx={{
            pb: 1,
            mb: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
          }}
        >
          <Grid item xs={12}>
            <Typography variant="h4" component="h2">
              {t("bulkUploadSoftScores")}
            </Typography>
          </Grid>
        </Grid>

        <Formik
          initialValues={{
            csvScores: "",
            csvScoresErrorMessage: "",
            csvScoresProgress: 0
          }}
          enableReinitialize
          onSubmit={async () => {
            return;
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values,
            setFieldValue
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    name="csvScores"
                    label={t("commaSeparatedList")}
                    value={values.csvScores}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={6}
                    margin="normal"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={Boolean(touched.csvScores && errors.csvScores)}
                    helperText={
                      <>
                        <Typography sx={{ fontSize: "inherit" }}>
                          {t("nameScoreFormat")}
                        </Typography>

                        {touched.csvScores && errors.csvScores ? (
                          <Typography
                            color="error"
                            sx={{ fontSize: "inherit" }}
                          >
                            {errors.csvScores}
                          </Typography>
                        ) : (
                          ""
                        )}
                      </>
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    color="primary"
                    variant="outlined"
                    fullWidth
                    onClick={async () => {
                      var unknownNames = [];
                      var csvScores = (values.csvScores || "")
                        .split("\n")
                        .map((str) => str.trim());
                      setFieldValue("csvScoresErrorMessage", "");
                      setFieldValue("csvScoresProgress", 0.01);

                      for (let i = 0; i < csvScores.length; i++) {
                        let csvScore = (csvScores[i] || "")
                          .split(",")
                          .map((str) => str.trim());
                        if (csvScore.length === 2) {
                          try {
                            setFieldValue(
                              "csvScoresProgress",
                              parseInt(
                                ((i * 2 + 1) / (csvScores.length * 2)) * 100 ||
                                  1
                              )
                            );
                            let player = players.filter(
                              (p) =>
                                csvScore[0] ===
                                `${p?.firstName || p?.user?.firstName} ${
                                  p?.lastName || p?.user?.lastName
                                }`
                            )?.[0];
                            if (player) {
                              await postData(
                                `/players/${player.id}/softScore`,
                                {
                                  body: {
                                    softScoreId: softScore?.id,
                                    value:
                                      softScore?.maxValue !== undefined &&
                                      parseFloat(csvScore[1]) >
                                        parseFloat(softScore?.maxValue)
                                        ? parseFloat(softScore?.maxValue)
                                        : parseFloat(csvScore[1])
                                  }
                                }
                              );
                            } else {
                              //
                            }
                            setFieldValue(
                              "csvScoresProgress",
                              parseInt(
                                ((i * 2 + 2) / (csvScores.length * 2)) * 100 ||
                                  1
                              )
                            );
                          } catch (err) {
                            //
                          }
                        }
                      }

                      setRefreshPlayers(true);
                      setFieldValue("csvScoresProgress", 0);

                      if (unknownNames.length) {
                        setFieldValue("csvScores", unknownNames.join("\n"));
                        setFieldValue(
                          "csvScoresErrorMessage",
                          t("someNamesNotLinked")
                        );
                        postNotification("error", t("someNamesNotLinked"));
                      } else {
                        setFieldValue("csvScores", "");
                        setOpen(false);
                      }
                    }}
                  >
                    {t("upload")}
                  </Button>
                  <ProgressModal
                    open={values.csvScoresProgress > 0}
                    value={values.csvScoresProgress}
                    message={t("uploadingPlayers")}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

function SoftScoreModal(props) {
  const { event, setEvent, softScore, setSoftScore } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventSoftScores"
  });
  const [processing, setProcessing] = useState(false);
  const confirm = useConfirm();
  return (
    <>
      <Modal
        disableAutoFocus
        open={Boolean(softScore)}
        onClose={() => {
          setSoftScore(null);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
        sx={{ zIndex: 9999 }}
      >
        {Boolean(softScore) ? (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: ["95vw", "85vw", 600],
              maxHeight: "95vh",
              overflowY: "scroll",
              bgcolor: "background.secondary",
              border: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 24,
              p: 4
            }}
          >
            <IconButton
              aria-label="cancel"
              color="error"
              sx={{ position: "absolute", top: "4px", right: "4px" }}
              onClick={() => {
                setSoftScore(null);
              }}
            >
              <BCPXmark />
            </IconButton>
            <Grid
              container
              spacing={2}
              sx={{
                pb: 1,
                mb: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
              }}
            >
              <Grid item xs={12}>
                <Typography variant="h4" component="h2">
                  Soft/Hobby Score Details
                </Typography>
              </Grid>
            </Grid>
            <Formik
              initialValues={{
                name: "",
                overall: false,
                multiplier: 1,
                maxValue: null,

                playerScored: false,

                type:
                  softScore?.playerScored && softScore?.oncePerEvent
                    ? "Once Per Event"
                    : softScore?.playerScored && softScore?.roundByRound
                      ? "Round by Round"
                      : null,
                roundByRoundType:
                  softScore?.roundByRoundOptions?.questions?.length > 0
                    ? "Questions"
                    : softScore?.playerScored && softScore?.roundByRound
                      ? "Range"
                      : null,

                roundByRoundDefaultValue:
                  softScore?.roundByRoundOptions?.defaultValue,
                roundByRoundMinValue: softScore?.roundByRoundOptions?.minValue,
                roundByRoundMaxValue: softScore?.roundByRoundOptions?.maxValue,
                roundByRoundQuestions:
                  softScore?.roundByRoundOptions?.questions,

                ...(softScore || {})
              }}
              enableReinitialize
              validationSchema={Yup.object().shape({
                name: Yup.string()
                  .min(3, "Minimum 3 Characters!")
                  .max(255, "Maximum 255 Characters!")
                  .required("Required"),
                multiplier: Yup.number().nullable(),
                maxValue: Yup.number().nullable()
              })}
              onSubmit={async (values) => {
                setProcessing(true);
                let softErrors = 0;
                try {
                  let sanitizedValues = { ...values };
                  if (softScore?.id) {
                    sanitizedValues.softScoreId = softScore.id;
                  }
                  for (let key of Object.keys(sanitizedValues)) {
                    if (sanitizedValues[key] === null) {
                      delete sanitizedValues[key];
                    }
                  }

                  sanitizedValues.oncePerEvent =
                    sanitizedValues.playerScored &&
                    sanitizedValues.type === "Once Per Event";
                  sanitizedValues.roundByRound =
                    sanitizedValues.playerScored &&
                    sanitizedValues.type === "Round by Round";

                  if (sanitizedValues.oncePerEvent) {
                    delete sanitizedValues.roundByRoundDefaultValue;
                    delete sanitizedValues.roundByRoundMinValue;
                    delete sanitizedValues.roundByRoundMaxValue;
                    delete sanitizedValues.roundByRoundType;
                    delete sanitizedValues.roundByRoundQuestions;
                  } else if (sanitizedValues.roundByRound) {
                    delete sanitizedValues.scoreOptions;
                    delete sanitizedValues.listType;

                    let floats = [
                      "roundByRoundDefaultValue",
                      "roundByRoundMinValue",
                      "roundByRoundMaxValue"
                    ];
                    for (let float of floats) {
                      if (sanitizedValues[float] === "") {
                        delete sanitizedValues[float];
                      } else if (sanitizedValues[float] !== undefined) {
                        sanitizedValues[float] = parseFloat(
                          sanitizedValues[float]
                        );
                      }
                    }

                    if (sanitizedValues.roundByRoundDefaultValue < 0) {
                      postNotification(
                        "error",
                        "Default value cannot be a negative value"
                      );
                      softErrors++;
                    }

                    if (sanitizedValues.roundByRoundType === "Range") {
                      delete sanitizedValues.roundByRoundQuestions;
                      if (sanitizedValues.roundByRoundMinValue === undefined) {
                        postNotification(
                          "error",
                          "Missing range minimum value"
                        );
                        softErrors++;
                      }
                      if (sanitizedValues.roundByRoundMaxValue === undefined) {
                        postNotification(
                          "error",
                          "Missing range maximum value"
                        );
                        softErrors++;
                      }
                      if (
                        sanitizedValues.roundByRoundMinValue !== undefined &&
                        sanitizedValues.roundByRoundMaxValue !== undefined &&
                        sanitizedValues.roundByRoundMinValue >=
                          sanitizedValues.roundByRoundMaxValue
                      ) {
                        postNotification(
                          "error",
                          "Range maximum value must be greater than the minimum value"
                        );
                        softErrors++;
                      }
                    } else if (
                      sanitizedValues.roundByRoundType === "Questions"
                    ) {
                      delete sanitizedValues.roundByRoundDefaultValue;
                      delete sanitizedValues.roundByRoundMinValue;
                      delete sanitizedValues.roundByRoundMaxValue;

                      if (!sanitizedValues.roundByRoundQuestions?.length) {
                        postNotification(
                          "error",
                          "Question soft scoring must have at least one question"
                        );
                        softErrors++;
                      } else {
                        for (let question of sanitizedValues.roundByRoundQuestions) {
                          if (!question.label || question.label.trim() === "") {
                            postNotification(
                              "error",
                              "All questions must have a label"
                            );
                            softErrors++;
                          }
                          if (!question.value) {
                            postNotification(
                              "error",
                              "All questions must have a value"
                            );
                            softErrors++;
                          }
                        }
                      }
                    }
                  }

                  if (softErrors > 0) {
                    setProcessing(false);
                    return;
                  }

                  if (
                    sanitizedValues.scoreOptions?.length > 1 &&
                    !isArraySorted(
                      sanitizedValues.scoreOptions.map((el) => el.value),
                      true
                    )
                  ) {
                    try {
                      await confirm({
                        title: `Your score values appear out of order`,
                        description: `Your score values appear to be out of order: ${sanitizedValues.scoreOptions
                          .map((el) => el.value)
                          .join(", ")}. Do you wish to continue?`,
                        dialogProps: {
                          sx: {
                            zIndex: 99999
                          }
                        }
                      });
                    } catch (err) {
                      setProcessing(false);
                      return;
                    }
                  }

                  let updatedEvent = await postData(
                    `/events/${event.id}/softScore`,
                    {
                      body: sanitizedValues
                    }
                  );
                  setEvent({ ...event, ...updatedEvent });
                  setSoftScore(null);
                } catch (err) {
                  postNotification("error", err);
                }
                setProcessing(false);
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                touched,
                values,
                setFieldValue
              }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        name="name"
                        label="Name"
                        value={values.name}
                        variant="outlined"
                        fullWidth
                        required
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="multiplier"
                        label="Multiplier"
                        value={values.multiplier}
                        variant="outlined"
                        fullWidth
                        required
                        type="number"
                        onBlur={handleChange}
                        onChange={handleChange}
                        error={Boolean(touched.multiplier && errors.multiplier)}
                        helperText={touched.multiplier && errors.multiplier}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        label="Effects Overall"
                        sx={{
                          ml: 0,
                          width: "100%",
                          "& .MuiFormControlLabel-label": {
                            width: "calc(100% - 58px)"
                          }
                        }}
                        control={
                          <Switch
                            checked={values.overall}
                            onChange={(e) => {
                              setFieldValue("overall", !values.overall);
                            }}
                          />
                        }
                        labelPlacement="start"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="maxValue"
                        label="Maximum Value (Optional)"
                        value={values.maxValue}
                        variant="outlined"
                        fullWidth
                        type="number"
                        onBlur={handleChange}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        label="Player Scored"
                        sx={{
                          ml: 0,
                          width: "100%",
                          "& .MuiFormControlLabel-label": {
                            width: "calc(100% - 58px)"
                          }
                        }}
                        control={
                          <Switch
                            checked={values.playerScored}
                            onChange={() => {
                              if (!values.playerScored) {
                                setFieldValue(
                                  "type",
                                  values.type || "Once Per Event"
                                );
                                setFieldValue(
                                  "listType",
                                  values.listType || "Checked In Players"
                                );
                                setFieldValue(
                                  "scoreOptions",
                                  values.scoreOptions?.length
                                    ? values.scoreOptions
                                    : [{ value: 1 }]
                                );
                              }
                              setFieldValue(
                                "playerScored",
                                !values.playerScored
                              );
                            }}
                          />
                        }
                        labelPlacement="start"
                      />
                    </Grid>
                    {values.playerScored && (
                      <>
                        <Grid item xs={12}>
                          <Alert
                            icon={false}
                            color="info"
                            sx={{
                              textAlign: "center",
                              justifyContent: "center"
                            }}
                          >
                            <AlertTitle sx={{ textTransform: "uppercase" }}>
                              Beta Feature
                            </AlertTitle>
                            Player Scored Soft Scores is currently a beta
                            version of Soft Scores with experimental features.
                            As a result, you may encounter issues. Some
                            additional reports are still required, any feedback
                            is appreciated via{" "}
                            <Link href="/support" target="_blank">
                              support
                            </Link>
                          </Alert>
                        </Grid>
                        <Grid item xs={12}>
                          <GenericPicker
                            name="type"
                            label="Soft Score Type"
                            value={values.type}
                            handleChange={(e) => {
                              if (
                                e.target.value &&
                                e.target.value !== values.type
                              ) {
                                handleChange(e);
                                if (e.target.value === "Once Per Event") {
                                  setFieldValue(
                                    "listType",
                                    values.listType || "Checked In Players"
                                  );
                                  setFieldValue(
                                    "scoreOptions",
                                    values.scoreOptions?.length
                                      ? values.scoreOptions
                                      : [{ value: 1 }]
                                  );
                                } else if (
                                  e.target.value === "Round by Round"
                                ) {
                                  setFieldValue("roundByRoundDefaultValue", "");
                                  setFieldValue("roundByRoundMinValue", 0);
                                  setFieldValue("roundByRoundMaxValue", "");
                                  setFieldValue("roundByRoundType", "Range");
                                }
                              }
                            }}
                            list={["Once Per Event", "Round by Round"]}
                            inclAny={false}
                          />
                        </Grid>
                        {values.type === "Once Per Event" && (
                          <>
                            <Grid item xs={12}>
                              <GenericPicker
                                name="listType"
                                label="Player List Type"
                                value={values.listType}
                                handleChange={handleChange}
                                list={[
                                  "All Players",
                                  "Checked In Players",
                                  "Opponents"
                                ]}
                                inclAny={false}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <FormControlLabel
                                label="Active"
                                sx={{
                                  ml: 0,
                                  width: "100%",
                                  "& .MuiFormControlLabel-label": {
                                    width: "calc(100% - 58px)"
                                  }
                                }}
                                control={
                                  <Switch
                                    checked={values.active}
                                    onChange={(e) => {
                                      setFieldValue("active", !values.active);
                                    }}
                                  />
                                }
                                labelPlacement="start"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <GenericPicker
                                name="numberOfScoreOptions"
                                label="Number of Scores"
                                value={values.scoreOptions?.length || 1}
                                handleChange={(e) => {
                                  let num = parseInt(e.target.value || 1);
                                  let newScoreOptions =
                                    values.scoreOptions || [];
                                  if (num > newScoreOptions.length) {
                                    while (num > newScoreOptions.length) {
                                      newScoreOptions.push({
                                        value: num - newScoreOptions.length
                                      });
                                    }
                                  } else if (num < newScoreOptions.length) {
                                    newScoreOptions = newScoreOptions.slice(
                                      0,
                                      num - 1
                                    );
                                  }
                                  setFieldValue(
                                    "scoreOptions",
                                    newScoreOptions
                                  );
                                }}
                                list={[1, 2, 3, 4, 5]}
                                inclAny={false}
                                required
                              />
                            </Grid>

                            {values.scoreOptions?.length > 0 && (
                              <FieldArray
                                name="scoreOptions"
                                render={(arrayHelpers) => (
                                  <>
                                    {values.scoreOptions.map(
                                      (scoreOption, index) => {
                                        return (
                                          <Grid key={index} item xs={12}>
                                            <TextField
                                              name={`scoreOptions[${index}].value`}
                                              label={`Score ${index + 1}`}
                                              value={
                                                values.scoreOptions?.[index]
                                                  ?.value
                                              }
                                              variant="outlined"
                                              fullWidth
                                              margin="dense"
                                              onBlur={handleBlur}
                                              onChange={handleChange}
                                              error={Boolean(
                                                touched.scoreOptions?.[index]
                                                  ?.value &&
                                                errors.scoreOptions?.[index]
                                                  ?.value
                                              )}
                                              helperText={
                                                touched.scoreOptions?.[index]
                                                  ?.value &&
                                                errors.scoreOptions?.[index]
                                                  ?.value
                                              }
                                              type="number"
                                              required
                                            />
                                          </Grid>
                                        );
                                      }
                                    )}
                                  </>
                                )}
                              />
                            )}
                          </>
                        )}
                        {values.type === "Round by Round" && (
                          <>
                            <Grid item xs={12}>
                              <GenericPicker
                                name="roundByRoundType"
                                label="Sub Type"
                                value={values.roundByRoundType}
                                handleChange={(e) => {
                                  if (
                                    e.target.value &&
                                    e.target.value !== values.roundByRoundType
                                  ) {
                                    handleChange(e);
                                    if (e.target.value === "Questions") {
                                      setFieldValue("roundByRoundQuestions", [
                                        { label: "", value: "" }
                                      ]);
                                    }
                                  }
                                }}
                                list={["Range", "Questions"]}
                                inclAny={false}
                              />
                            </Grid>

                            {values.roundByRoundType === "Range" && (
                              <>
                                <Grid item xs={12}>
                                  <TextField
                                    name="roundByRoundDefaultValue"
                                    label="Default Value (Optional)"
                                    value={values.roundByRoundDefaultValue}
                                    variant="outlined"
                                    fullWidth
                                    type="number"
                                    onBlur={handleChange}
                                    onChange={handleChange}
                                  />
                                  <FormHelperText>
                                    This is the score used if the Player does
                                    not submit a soft score for their opponent.
                                  </FormHelperText>
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    name="roundByRoundMinValue"
                                    label="Minimum Value"
                                    required
                                    value={values.roundByRoundMinValue}
                                    variant="outlined"
                                    fullWidth
                                    type="number"
                                    onBlur={handleChange}
                                    onChange={handleChange}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    name="roundByRoundMaxValue"
                                    label="Maximum Value"
                                    required
                                    value={values.roundByRoundMaxValue}
                                    variant="outlined"
                                    fullWidth
                                    type="number"
                                    onBlur={handleChange}
                                    onChange={handleChange}
                                  />
                                </Grid>
                              </>
                            )}
                            {values.roundByRoundType === "Questions" && (
                              <FieldArray
                                name="roundByRoundQuestions"
                                render={(arrayHelpers) => (
                                  <>
                                    {values.roundByRoundQuestions?.length > 0 &&
                                      values.roundByRoundQuestions.map(
                                        (question, index) => {
                                          return (
                                            <>
                                              <Grid
                                                key={"label." + index}
                                                item
                                                xs={8}
                                              >
                                                <TextField
                                                  name={`roundByRoundQuestions[${index}].label`}
                                                  label={`Question ${
                                                    index + 1
                                                  }`}
                                                  value={
                                                    values
                                                      .roundByRoundQuestions?.[
                                                      index
                                                    ]?.label
                                                  }
                                                  variant="outlined"
                                                  fullWidth
                                                  margin="dense"
                                                  onBlur={handleBlur}
                                                  onChange={handleChange}
                                                  error={Boolean(
                                                    touched
                                                      .roundByRoundQuestions?.[
                                                      index
                                                    ]?.label &&
                                                    errors
                                                      .roundByRoundQuestions?.[
                                                      index
                                                    ]?.label
                                                  )}
                                                  helperText={
                                                    touched
                                                      .roundByRoundQuestions?.[
                                                      index
                                                    ]?.label &&
                                                    errors
                                                      .roundByRoundQuestions?.[
                                                      index
                                                    ]?.label
                                                  }
                                                  required
                                                />
                                              </Grid>
                                              <Grid
                                                key={"value." + index}
                                                item
                                                xs={4}
                                              >
                                                <TextField
                                                  name={`roundByRoundQuestions[${index}].value`}
                                                  label={`Value ${index + 1}`}
                                                  value={
                                                    values
                                                      .roundByRoundQuestions?.[
                                                      index
                                                    ]?.value
                                                  }
                                                  variant="outlined"
                                                  fullWidth
                                                  margin="dense"
                                                  onBlur={handleBlur}
                                                  onChange={handleChange}
                                                  error={Boolean(
                                                    touched
                                                      .roundByRoundQuestions?.[
                                                      index
                                                    ]?.value &&
                                                    errors
                                                      .roundByRoundQuestions?.[
                                                      index
                                                    ]?.value
                                                  )}
                                                  helperText={
                                                    touched
                                                      .roundByRoundQuestions?.[
                                                      index
                                                    ]?.value &&
                                                    errors
                                                      .roundByRoundQuestions?.[
                                                      index
                                                    ]?.value
                                                  }
                                                  type="number"
                                                  required
                                                  InputProps={{
                                                    endAdornment: (
                                                      <InputAdornment position="end">
                                                        <Button
                                                          sx={{
                                                            minWidth: "40px"
                                                          }}
                                                          onClick={() => {
                                                            arrayHelpers.remove(
                                                              index
                                                            );
                                                          }}
                                                          disabled={
                                                            values
                                                              .roundByRoundQuestions
                                                              .length <= 2
                                                          }
                                                        >
                                                          <BCPXmark />
                                                        </Button>
                                                      </InputAdornment>
                                                    )
                                                  }}
                                                />
                                              </Grid>
                                            </>
                                          );
                                        }
                                      )}
                                    <Grid item xs={12}>
                                      <Button
                                        type="button"
                                        color="inherit"
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<BCPSquarePlus />}
                                        onClick={() => {
                                          arrayHelpers.push({
                                            label: "",
                                            value: ""
                                          });
                                        }}
                                      >
                                        Add Question
                                      </Button>
                                    </Grid>
                                  </>
                                )}
                              />
                            )}
                          </>
                        )}
                      </>
                    )}

                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        color="success"
                        variant="contained"
                        type="submit"
                      >
                        {t("save")}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </Box>
        ) : (
          <></>
        )}
      </Modal>
      {processing && <ModalLoadingFull />}
    </>
  );
}

function isArraySorted(arr, desc) {
  let sorted = true;
  for (let i = 0; i < arr.length - 1; i++) {
    if ((desc && arr[i] < arr[i + 1]) || (!desc && arr[i] > arr[i + 1])) {
      sorted = false;
      break;
    }
  }
  return sorted;
}
